// ══════════════════════════════════════════════════════════════════════════
// SalesTabs.jsx — the Sales and Audit views
//
// Both take plain props and own no persistence, so PricingDashboard keeps
// control of state, Supabase and the single upload/Inputs section.
//
//   salesWeeks     { [store]: { [mondayISO]: { [catapultId]: {qty,amt,name,sub} } } }
//   catalogIndex   { [store]: [ {d365,cid,name,status} ] }   from parseCatalogCatapult
//   listPrices     from parseListPrices
//
// Uses the same visual tokens as the pricing view (var(--bg-primary) etc.) so
// it reads as one app.
// ══════════════════════════════════════════════════════════════════════════

import { useState, useMemo, useRef, Component, useCallback } from "react";
import {
  indexCatalog, resolveStatus, STATUS, lookupListPrice,
  priceGapPct, toCents, buildSalesAndAudit,
} from "./salesData";

const C = {
  blue: "#4a7cff", red: "#e05252", grn: "#22c55e", amb: "#f59e0b", vio: "#a78bfa",
  mono: "'DM Mono', ui-monospace, monospace",
};
const ST_META = {
  [STATUS.MATCHED]:        { label: "In catalog",     color: C.grn, desc: "Sales ID matches the catalog's Catapult ID." },
  [STATUS.ID_MISMATCH]:    { label: "ID mismatch",    color: C.amb, desc: "Selling on the D365 number while the catalog assigns a different Catapult ID. A fixable mapping error — the match is exact, not a guess." },
  [STATUS.NOT_IN_CATALOG]: { label: "Not in catalog", color: C.red, desc: "No catalog match found. Either a scale item you don't purchase against, or an unmapped ID." },
  unknown:                 { label: "No catalog",     color: "#585d6e", desc: "This store's catalog hasn't been loaded, so reconciliation is unknown — not a problem with the item." },
};

const money = (v) => v == null ? "—" : "$" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qf = (v) => v == null ? "—" : (Math.abs(v - Math.round(v)) < 1e-9 ? String(Math.round(v)) : Number(v).toFixed(2));

/** Sorted week keys (Mondays) present for a store, or across all stores. */
function weeksFor(salesWeeks, store) {
  const set = new Set();
  Object.entries(salesWeeks || {}).forEach(([s, byWeek]) => {
    if (store !== "ALL" && s !== store) return;
    Object.keys(byWeek || {}).forEach((w) => set.add(w));
  });
  return [...set].sort();
}

/**
 * Flatten stored weeks into one row per store+item, carrying the full week
 * series so the detail chart can show more than two points.
 */
function buildRows({ salesWeeks, catalogIndex, listPrices, store, weeks }) {
  const out = [];
  const stores = store === "ALL" ? Object.keys(salesWeeks || {}) : [store];
  for (const s of stores) {
    const idx = indexCatalog(catalogIndex?.[s]);
    const hasCat = idx.items.length > 0;
    const byWeek = salesWeeks?.[s] || {};
    const pids = new Set();
    Object.values(byWeek).forEach((wk) => Object.keys(wk).forEach((p) => pids.add(p)));
    for (const pid of pids) {
      const series = weeks.map((w) => byWeek[w]?.[pid] || null);
      const latest = [...series].reverse().find(Boolean);
      if (!latest) continue;
      const { status, rec } = hasCat ? resolveStatus(pid, idx) : { status: null, rec: null };
      const keys = rec?.d365 ? [rec.d365, rec.d365.replace(/^0+/, "")] : [pid, pid.replace(/^0+/, "")];
      const { listPrice, placeholder } = lookupListPrice(s, keys, listPrices);
      const qty = series.map((x) => (x ? x.qty : null));
      const amt = series.map((x) => (x ? toCents(x.amt) : null));
      const n = series.length;
      out.push({
        key: s + "|" + pid, store: s, pid, d365: rec?.d365 || "",
        name: latest.name, sub: latest.sub,
        catalogName: rec && rec.name && rec.name.toUpperCase() !== latest.name.toUpperCase() ? rec.name : "",
        qty, amt, qtyCur: qty[n - 1], amtCur: amt[n - 1], qtyPrev: qty[n - 2] ?? null, amtPrev: amt[n - 2] ?? null,
        listPrice, placeholder, status: status || "unknown",
      });
    }
  }
  return out;
}

function delta(prev, cur) {
  if (prev == null && cur == null) return { k: "none" };
  if (prev == null) return { k: "new" };
  if (cur == null) return { k: "gone" };
  if (prev === 0) return cur === 0 ? { k: "pct", v: 0 } : { k: "new" };
  return { k: "pct", v: ((cur - prev) / Math.abs(prev)) * 100 };
}
function Delta({ d }) {
  if (d.k === "new") return <span style={{ color: C.blue, fontSize: 11, fontWeight: 600 }}>new</span>;
  if (d.k === "gone") return <span style={{ color: "#585d6e", fontSize: 11, fontWeight: 600 }}>no sales</span>;
  if (d.k === "none") return <span style={{ color: "#585d6e", fontSize: 11 }}>—</span>;
  const up = d.v > 0.05, dn = d.v < -0.05;
  return <span style={{ color: up ? C.grn : dn ? C.red : "#585d6e", fontSize: 11, fontFamily: C.mono }}>
    {up ? "↑" : dn ? "↓" : "→"} {Math.abs(d.v).toFixed(0)}%
  </span>;
}

// ── chart: breaks at gaps, floors the axis span so rounding noise stays flat
// Crosshair: hovering anywhere over the plot snaps to the nearest week and reads
// out every series at that week. A missing week reads "—", never 0 — the chart
// already breaks its line at gaps and the readout has to say the same thing.
function Spark({ title, series, weeks, color, fmt, minSpan, dashSeries, legend }) {
  const w = 600, h = 132, pad = { t: 14, r: 16, b: 26, l: 60 };
  const iw = w - pad.l - pad.r, ih = h - pad.t - pad.b;
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const all = [...series, ...(dashSeries || [])].filter((v) => v != null);
  if (!all.length) return null;
  let lo = Math.min(...all), hi = Math.max(...all);
  const mid = (lo + hi) / 2, ms = minSpan ? minSpan(Math.abs(mid)) : 0;
  let sp = hi - lo;
  if (sp < ms) { lo = mid - ms / 2; hi = mid + ms / 2; sp = ms; }
  if (sp <= 0) sp = Math.abs(hi) || 1;
  const yMin = lo - sp * 0.25, yMax = hi + sp * 0.25;
  const n = series.length;
  const X = (i) => n === 1 ? pad.l + iw / 2 : pad.l + (i / (n - 1)) * iw;
  const Y = (v) => pad.t + ih - ((v - yMin) / (yMax - yMin || 1)) * ih;
  const segs = (vals) => {
    const out = []; let cur = [];
    vals.forEach((v, i) => { if (v != null) cur.push([X(i), Y(v)]); else { if (cur.length) out.push(cur); cur = []; } });
    if (cur.length) out.push(cur);
    return out;
  };

  // Client pixels → viewBox units → nearest week index.
  const track = (clientX) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || !r.width) return;
    if (n === 1) { setHover(0); return; }
    const vx = ((clientX - r.left) / r.width) * w;
    const i = Math.round(((vx - pad.l) / iw) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };

  // Tooltip contents — primary series, plus the dashed comparison when present.
  const hv = hover != null ? series[hover] : null;
  const dv = hover != null && dashSeries ? dashSeries[hover] : null;
  const lines = hover == null ? [] : [
    { label: legend?.[0]?.[0] || title, value: hv == null ? "\u2014" : fmt(hv), color },
    ...(dashSeries ? [{ label: legend?.[1]?.[0] || "comparison", value: dv == null ? "\u2014" : fmt(dv), color: C.vio }] : []),
  ];
  const boxW = Math.max(132, ...lines.map((l) => 14 + (l.label.length + String(l.value).length) * 5.6));
  const boxH = 22 + lines.length * 16;
  const hx = hover == null ? 0 : X(hover);
  const bx = hx + boxW + 14 > w ? hx - boxW - 10 : hx + 10;
  const by = Math.min(pad.t + 2, pad.t + ih - boxH);

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "0 2px 2px" }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color }}>{title}</span>
        {legend && <span style={{ display: "flex", gap: 12, fontSize: 10 }}>
          {legend.map(([l, c]) => <span key={l} style={{ color: c }}>— {l}</span>)}</span>}
      </div>
      <svg ref={svgRef} viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", display: "block" }}
        onMouseMove={(e) => track(e.clientX)}
        onMouseLeave={() => setHover(null)}
        onTouchStart={(e) => e.touches[0] && track(e.touches[0].clientX)}
        onTouchMove={(e) => e.touches[0] && track(e.touches[0].clientX)}
        onTouchEnd={() => setHover(null)}>
        {[0, 1, 2, 3].map((k) => {
          const v = yMin + ((yMax - yMin) * k) / 3;
          return <g key={k}>
            <line x1={pad.l} y1={Y(v)} x2={pad.l + iw} y2={Y(v)} stroke="#222536" strokeDasharray="3 4" />
            <text x={pad.l - 8} y={Y(v) + 3.5} textAnchor="end" fill="#585d6e" fontSize={9.5} fontFamily={C.mono}>{fmt(v)}</text>
          </g>;
        })}
        {weeks.map((wk, i) => (
          <text key={wk} x={X(i)} y={h - 8} textAnchor="middle" fontFamily={C.mono}
            fill={hover === i ? "var(--text-primary, #e8eaf0)" : "#585d6e"}
            fontWeight={hover === i ? 700 : 400} fontSize={9.5}>{wk.slice(5)}</text>
        ))}

        {/* crossbars — vertical at the week, horizontal at that week's value */}
        {hover != null && <g>
          <line x1={hx} y1={pad.t} x2={hx} y2={pad.t + ih} stroke="#585d6e" strokeWidth={1} strokeDasharray="2 3" />
          {hv != null && <line x1={pad.l} y1={Y(hv)} x2={pad.l + iw} y2={Y(hv)} stroke={color} strokeWidth={1} strokeDasharray="2 3" opacity={0.5} />}
        </g>}

        {dashSeries && segs(dashSeries).map((s, i) => s.length > 1 && (
          <polyline key={"d" + i} fill="none" stroke={C.vio} strokeWidth={2} strokeDasharray="5 3"
            points={s.map((p) => p.join(",")).join(" ")} />
        ))}
        {segs(series).map((s, i) => <g key={i}>
          {s.length > 1 && <polyline fill="none" stroke={color} strokeWidth={2} points={s.map((p) => p.join(",")).join(" ")} />}
          {s.map((p, j) => <circle key={j} cx={p[0]} cy={p[1]} r={3.5} fill={color} stroke="var(--bg-primary)" strokeWidth={1.5} />)}
        </g>)}
        {hover != null && hv != null && <circle cx={hx} cy={Y(hv)} r={5.5} fill={color} stroke="var(--bg-primary)" strokeWidth={2} />}
        {hover != null && dv != null && <circle cx={hx} cy={Y(dv)} r={4.5} fill={C.vio} stroke="var(--bg-primary)" strokeWidth={1.5} />}

        {hover != null && <g pointerEvents="none">
          <rect x={bx} y={by} width={boxW} height={boxH} rx={7} fill="#1c1f2b" stroke="#2a2d3a" />
          <text x={bx + 10} y={by + 15} fill="#e8eaf0" fontSize={10.5} fontWeight={700}>Week of {weeks[hover]}</text>
          {lines.map((l, i) => (
            <text key={l.label} x={bx + 10} y={by + 31 + i * 16} fontSize={10.5} fontFamily={C.mono} fill={l.color}>
              {l.label}: {l.value}
            </text>
          ))}
        </g>}

        {/* transparent capture layer — guarantees hover works over empty plot area */}
        <rect x={pad.l - 6} y={pad.t} width={iw + 12} height={ih + 14} fill="transparent" />
      </svg>
    </div>
  );
}

function StatBlock({ title, color, vals, fmt }) {
  const ok = vals.filter((v) => v != null);
  if (!ok.length) return <div style={{ padding: "12px 16px", borderRight: "1px solid var(--border)" }}>
    <h4 style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase", color }}>{title}</h4>
    <div style={{ color: "#585d6e", fontSize: 11 }}>No data</div></div>;
  const mn = Math.min(...ok), mx = Math.max(...ok);
  const avg = ok.reduce((a, b) => a + b, 0) / ok.length;
  const pc = ok[0] ? ((ok[ok.length - 1] - ok[0]) / Math.abs(ok[0])) * 100 : null;
  const row = (l, v) => <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontFamily: C.mono, marginBottom: 3 }}>
    <span style={{ color: "#585d6e", fontFamily: "inherit", fontSize: 10.5 }}>{l}</span><span>{v}</span></div>;
  return <div style={{ padding: "12px 16px", borderRight: "1px solid var(--border)" }}>
    <h4 style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color }}>
      {title} <span style={{ color: "#585d6e", fontWeight: 400 }}>({ok.length} pt{ok.length > 1 ? "s" : ""})</span></h4>
    {row("Min", fmt(mn))}{row("Max", fmt(mx))}{row("Range", fmt(mx - mn))}{row("Average", fmt(avg))}
    <div style={{ borderTop: "1px solid var(--border)", marginTop: 7, paddingTop: 6 }}>
      {row("% Change", pc == null ? "—" : (pc > 0 ? "+" : "") + pc.toFixed(1) + "%")}</div>
  </div>;
}

// If a modal ever throws mid-render, React unmounts the whole tab and the screen
// goes blank — which looks exactly like "the click did nothing". This catches it
// and says what happened instead.
class ModalBoundary extends Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div onClick={this.props.onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-panel, #161820)", border: `1px solid ${C.red}55`, borderRadius: 12, width: 520, maxWidth: "95vw", padding: "20px 24px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.red, marginBottom: 8 }}>This view couldn't render</div>
          <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.55, marginBottom: 12 }}>
            The data behind it loaded, but drawing it failed. The message below is the actual error — worth sending on.
          </div>
          <div style={{ fontFamily: C.mono, fontSize: 11, color: C.amb, background: "#1c1f2b", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 12px", wordBreak: "break-word" }}>
            {String(this.state.err?.message || this.state.err)}
          </div>
          <button onClick={this.props.onClose} style={{ marginTop: 14, padding: "6px 14px", borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", fontSize: 12, cursor: "pointer" }}>Close</button>
        </div>
      </div>
    );
  }
}

// Department totals across every loaded week. Uses the rows currently on screen,
// so whatever is filtered is what's totalled — and the scope is spelled out in
// the header so the numbers are never ambiguous.
function TotalsDetail({ rows, weeks, store, scope, focus, onClose }) {
  const net = weeks.map((_, i) => {
    let t = 0, any = false;
    rows.forEach((r) => { const v = r.amt[i]; if (v != null) { t += v; any = true; } });
    return any ? toCents(t) : null;
  });
  const qty = weeks.map((_, i) => {
    let t = 0, any = false;
    rows.forEach((r) => { const v = r.qty[i]; if (v != null) { t += v; any = true; } });
    return any ? toCents(t) : null;
  });
  // How many distinct items sold that week — a revenue rise driven by more items
  // selling is a different story from the same items selling more.
  const items = weeks.map((_, i) => rows.filter((r) => r.amt[i] != null).length);

  const netChart = { title: "Net amount", series: net, color: C.grn,
    fmt: (v) => "$" + Math.round(v).toLocaleString(), minSpan: (m) => Math.max(4, m * 0.06) };
  const qtyChart = { title: "Quantity sold", series: qty, color: C.blue,
    fmt: qf, minSpan: (m) => Math.max(2, m * 0.06) };
  const order = focus === "qty" ? [qtyChart, netChart] : [netChart, qtyChart];

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-panel, #161820)", border: "1px solid var(--border)", borderRadius: 12, width: 700, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Department totals · {store === "ALL" ? "All stores" : store}</div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 4, fontFamily: C.mono }}>
              {rows.length} items · {weeks.length} week{weeks.length !== 1 ? "s" : ""} · {scope}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-secondary)", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: "16px 22px 6px" }}>
          {order.map((c) => (
            <Spark key={c.title} title={c.title} series={c.series} weeks={weeks}
                   color={c.color} fmt={c.fmt} minSpan={c.minSpan} />
          ))}
          <Spark title="Items with sales" series={items} weeks={weeks} color={C.vio}
                 fmt={(v) => String(Math.round(v))} minSpan={(m) => Math.max(2, m * 0.06)} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(158px,1fr))", borderTop: "1px solid var(--border)", background: "#1c1f2b" }}>
          <StatBlock title="Net amount" color={C.grn} vals={net} fmt={(v) => money(v)} />
          <StatBlock title="Quantity" color={C.blue} vals={qty} fmt={qf} />
          <StatBlock title="Items with sales" color={C.vio} vals={items} fmt={(v) => String(Math.round(v))} />
        </div>
        {weeks.length < 4 && (
          <div style={{ padding: "12px 22px", borderTop: "1px solid var(--border)", fontSize: 11.5, color: "#585d6e", lineHeight: 1.5 }}>
            Only {weeks.length} week{weeks.length !== 1 ? "s" : ""} loaded — this fills out as weekly files accumulate.
          </div>
        )}
      </div>
    </div>
  );
}

function SalesDetail({ row, weeks, onClose }) {
  const cap = row.qty.map((q, i) => (q && row.amt[i] != null && q !== 0 ? toCents(row.amt[i] / q) : null));
  const lp = toCents(row.listPrice);
  const listSeries = weeks.map(() => (lp != null ? lp : null));
  const m = ST_META[row.status];
  const gap = priceGapPct({ qty: row.qtyCur, amt: row.amtCur, listPrice: row.listPrice, placeholder: row.placeholder });
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-panel, #161820)", border: "1px solid var(--border)", borderRadius: 12, width: 700, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between" }}>
          <div><div style={{ fontSize: 16, fontWeight: 700 }}>{row.name}</div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 4, fontFamily: C.mono }}>
              {row.store} · Catapult {row.pid}{row.d365 ? ` · D365 ${row.d365}` : ""} · {row.sub}</div></div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-secondary)", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: "14px 22px", borderBottom: "1px solid #1a1d24" }}>
          <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 5, background: m.color + "1a", border: `1px solid ${m.color}44`, color: m.color, fontSize: 10.5, fontWeight: 700 }}>{m.label}</span>
          <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 8, lineHeight: 1.55 }}>{m.desc}</div>
          {row.catalogName && <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 6 }}>
            Catalog calls it <span style={{ color: "var(--text-primary)", fontFamily: C.mono }}>{row.catalogName}</span></div>}
          {row.placeholder && <div style={{ fontSize: 11.5, color: C.amb, marginTop: 6 }}>
            No real price set in D365 — the agreement is a $0.001 placeholder, yet this item is selling.</div>}
          {gap != null && Math.abs(gap) >= 8 && <div style={{ fontSize: 11.5, color: gap < 0 ? C.red : C.amb, marginTop: 6 }}>
            Captured {Math.abs(gap).toFixed(0)}% {gap < 0 ? "below" : "above"} the list price of {money(lp)}
            {gap < 0 ? " — markdowns or promo pricing." : " — the new price likely never took in the scale."}</div>}
        </div>
        <div style={{ padding: "16px 22px 6px" }}>
          <Spark title="Quantity sold" series={row.qty} weeks={weeks} color={C.blue} fmt={qf} minSpan={(m2) => Math.max(2, m2 * 0.06)} />
          <Spark title="Net amount" series={row.amt} weeks={weeks} color={C.grn} fmt={(v) => "$" + v.toFixed(0)} minSpan={(m2) => Math.max(4, m2 * 0.06)} />
          <Spark title="Unit price" series={cap} dashSeries={listSeries} weeks={weeks} color={C.amb}
            fmt={(v) => "$" + v.toFixed(2)} minSpan={(m2) => Math.max(0.25, m2 * 0.08)}
            legend={[["captured (net ÷ qty)", C.amb], ["list price", C.vio]]} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(158px,1fr))", borderTop: "1px solid var(--border)", background: "#1c1f2b" }}>
          <StatBlock title="Quantity" color={C.blue} vals={row.qty} fmt={qf} />
          <StatBlock title="Net amount" color={C.grn} vals={row.amt} fmt={(v) => "$" + v.toFixed(2)} />
          <StatBlock title="Captured price" color={C.amb} vals={cap} fmt={(v) => "$" + v.toFixed(2)} />
          <StatBlock title="List price" color={C.vio} vals={listSeries} fmt={(v) => "$" + v.toFixed(2)} />
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SALES TAB
// ══════════════════════════════════════════════════════════════════════════
// ── Row selection ────────────────────────────────────────
// Selection is keyed on the row's own identity, not its position, so ticking a
// row and then sorting or filtering keeps the same items selected. Sales rows
// key on store + Catapult ID. Audit rows fall back through Catapult ID → sales
// Product Id → D365, because the whole point of that tab is rows where one of
// those is missing.
function useRowSelection(rows, keyOf) {
  const [sel, setSel] = useState(() => new Set());
  const visible = useMemo(() => rows.map(keyOf), [rows, keyOf]);
  const selectedVisible = useMemo(() => visible.filter((k) => sel.has(k)), [visible, sel]);
  const allOn = visible.length > 0 && selectedVisible.length === visible.length;

  const toggle = (k) => setSel((prev) => {
    const n = new Set(prev);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });
  // Select-all applies to what's on screen only; it never reaches rows hidden
  // by the current filter.
  const toggleAll = () => setSel((prev) => {
    const n = new Set(prev);
    if (allOn) visible.forEach((k) => n.delete(k));
    else visible.forEach((k) => n.add(k));
    return n;
  });
  const clear = () => setSel(new Set());
  return { sel, count: selectedVisible.length, allOn, toggle, toggleAll, clear };
}

const cbStyle = { accentColor: "#5b8dd9", width: 14, height: 14, cursor: "pointer", verticalAlign: "middle" };

function SelectionBar({ count, total, onClear, label = "rows" }) {
  if (!count) return null;
  return (
    <span style={{ fontSize: 11.5, color: C.blue, fontWeight: 600 }}>
      {count} of {total} {label} selected
      <button onClick={onClear} style={{ marginLeft: 8, padding: "2px 8px", borderRadius: 6, border: "1px solid var(--border)",
        background: "transparent", color: "var(--text-secondary)", fontSize: 10.5, cursor: "pointer" }}>clear</button>
    </span>
  );
}

export function SalesTab({ salesWeeks, catalogIndex, listPrices, store, onExport }) {
  const [filter, setFilter] = useState("all");
  const [subSel, setSubSel] = useState([]);
  const [subOpen, setSubOpen] = useState(false);
  const [subQ, setSubQ] = useState("");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("amt");
  const [sortDir, setSortDir] = useState(-1);
  const [detail, setDetail] = useState(null);
  const [totalsFocus, setTotalsFocus] = useState(null);   // "net" | "qty" | null

  const weeks = useMemo(() => weeksFor(salesWeeks, store), [salesWeeks, store]);
  const all = useMemo(() => buildRows({ salesWeeks, catalogIndex, listPrices, store, weeks }), [salesWeeks, catalogIndex, listPrices, store, weeks]);

  const counts = useMemo(() => {
    const c = { all: all.length, [STATUS.MATCHED]: 0, [STATUS.ID_MISMATCH]: 0, [STATUS.NOT_IN_CATALOG]: 0, unknown: 0, gap: 0 };
    all.forEach((r) => {
      c[r.status] = (c[r.status] || 0) + 1;
      const g = priceGapPct({ qty: r.qtyCur, amt: r.amtCur, listPrice: r.listPrice, placeholder: r.placeholder });
      if (g != null && Math.abs(g) >= 8) c.gap++;
    });
    return c;
  }, [all]);

  const base = useMemo(() => {
    let out = all.filter((r) => {
      if (filter === "all") return true;
      if (filter === "gap") {
        const g = priceGapPct({ qty: r.qtyCur, amt: r.amtCur, listPrice: r.listPrice, placeholder: r.placeholder });
        return g != null && Math.abs(g) >= 8;
      }
      return r.status === filter;
    });
    if (q.trim()) {
      const k = q.trim().toLowerCase();
      out = out.filter((r) => r.name.toLowerCase().includes(k) || r.pid.includes(k) || (r.d365 || "").includes(k));
    }
    return out;
  }, [all, filter, q]);

  const rows = useMemo(() => {
    let out = subSel.length ? base.filter((r) => subSel.includes(r.sub)) : base;
    const val = (r) => ({
      store: r.store, status: r.status, id: r.pid, name: r.name, sub: r.sub,
      qty: r.qtyCur, amt: r.amtCur,
      qtyd: delta(r.qtyPrev, r.qtyCur).v ?? null,
      amtd: delta(r.amtPrev, r.amtCur).v ?? null,
      cap: r.qtyCur && r.amtCur != null && r.qtyCur !== 0 ? toCents(r.amtCur / r.qtyCur) : null,
      list: r.listPrice,
      gap: (() => { const g = priceGapPct({ qty: r.qtyCur, amt: r.amtCur, listPrice: r.listPrice, placeholder: r.placeholder }); return g == null ? null : Math.abs(g); })(),
    })[sortKey];
    return [...out].sort((a, b) => {
      const x = val(a), y = val(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;            // nulls always sink
      if (y == null) return -1;
      return typeof x === "string" ? sortDir * x.localeCompare(y) : sortDir * (x - y);
    });
  }, [base, subSel, sortKey, sortDir]);

  const subOpts = useMemo(() => {
    const c = {};
    base.forEach((r) => { c[r.sub] = (c[r.sub] || 0) + 1; });
    let opts = [...new Set(all.map((r) => r.sub))].filter(Boolean).sort().map((n) => ({ name: n, n: c[n] || 0 }));
    const k = subQ.trim().toLowerCase();
    if (k) opts = opts.filter((o) => o.name.toLowerCase().includes(k))
      .sort((a, b) => (a.name.toLowerCase().indexOf(k) === 0 ? 0 : 1) - (b.name.toLowerCase().indexOf(k) === 0 ? 0 : 1) || a.name.localeCompare(b.name));
    return opts;
  }, [all, base, subQ]);

  const salesKeyOf = useCallback((r) => r.key, []);
  const pick = useRowSelection(rows, salesKeyOf);

  // "No list price" has two very different causes: the export was never loaded,
  // or the item isn't on it. A bare dash can't tell them apart, so say which.
  const lpMissing = useMemo(() => {
    const covered = Object.keys(listPrices || {});
    if (!covered.length) return "none";
    if (store !== "ALL") return covered.includes(store) ? null : "store";
    const shown = [...new Set(rows.map((r) => r.store))];
    const gaps = shown.filter((s) => !covered.includes(s));
    return gaps.length ? gaps.join(", ") : null;
  }, [listPrices, store, rows]);

  const tot = useMemo(() => {
    const t = { a: 0, q: 0, ap: 0, qp: 0 };
    rows.forEach((r) => { t.a += r.amtCur || 0; t.q += r.qtyCur || 0; t.ap += r.amtPrev || 0; t.qp += r.qtyPrev || 0; });
    return t;
  }, [rows]);

  const th = (label, key, align) => (
    <th onClick={key ? () => { if (sortKey === key) setSortDir(-sortDir); else { setSortKey(key); setSortDir(["name", "sub", "id", "status", "store"].includes(key) ? 1 : -1); } } : undefined}
      style={{ textAlign: align || "left", padding: "9px 10px", fontSize: 10, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: sortKey === key ? C.blue : "var(--text-secondary)", cursor: key ? "pointer" : "default", whiteSpace: "nowrap" }}>
      {label}{sortKey === key ? (sortDir < 0 ? " ▼" : " ▲") : ""}
    </th>
  );
  const pill = (key, label, color, n) => (
    <button key={key} onClick={() => setFilter(key)}
      style={{ padding: "5px 12px", borderRadius: 7, fontSize: 11.5, fontWeight: filter === key ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap",
        border: `1px solid ${filter === key ? (color || C.blue) : "var(--border)"}`,
        background: filter === key ? (color || C.blue) + "1a" : "transparent",
        color: filter === key ? (color || C.blue) : "var(--text-secondary)" }}>
      {label} <span style={{ fontFamily: C.mono, opacity: .75 }}>{n}</span>
    </button>
  );

  if (!weeks.length) return (
    <div style={{ padding: 40, textAlign: "center", color: "var(--text-secondary)" }}>
      No sales data yet. Upload a <span style={{ fontFamily: C.mono }}>Product Sales_MMDDYY</span> file — the drop zone above feeds every tab.
    </div>
  );

  return (
    <div style={{ padding: "16px 28px 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 11.5, color: "var(--text-secondary)" }}>
          {store === "ALL" ? "All stores" : store} · {weeks.length} week{weeks.length !== 1 ? "s" : ""} ·{" "}
          {weeks[0]?.slice(5)} → {weeks[weeks.length - 1]?.slice(5)} · {rows.length} items shown · click any row for history
        </div>
        <div style={{ display: "flex", gap: 14 }}>
          {[["Net amount", money(toCents(tot.a)), delta(tot.ap, tot.a), C.grn, "net"],
            ["Quantity sold", qf(toCents(tot.q)), delta(tot.qp, tot.q), C.blue, "qty"]].map(([lab, val, d, col, key]) => (
            <button key={lab} type="button" onClick={() => setTotalsFocus(key)} title="Open week-by-week totals"
              style={{ background: "var(--bg-panel, #161820)", border: "1px solid var(--border)", borderRadius: 10,
                padding: "11px 16px", minWidth: 172, cursor: "pointer", position: "relative", textAlign: "left",
                font: "inherit", color: "inherit", display: "block", transition: "border-color .15s, background .15s" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#585d6e"; e.currentTarget.style.background = "#191c25"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "var(--bg-panel, #161820)"; }}>
              <span style={{ position: "absolute", top: 9, right: 11, fontSize: 9.5, fontWeight: 700, letterSpacing: ".4px", color: col, opacity: .8 }}>trend ›</span>
              <div style={{ fontSize: 9.5, color: "#585d6e", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 700 }}>{lab}</div>
              <div style={{ fontFamily: C.mono, fontSize: 22, fontWeight: 700, color: col, marginTop: 3 }}>{val}</div>
              <div style={{ marginTop: 4 }}><Delta d={d} /></div>
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        {pill("all", "All items", null, counts.all)}
        {counts[STATUS.MATCHED] > 0 && pill(STATUS.MATCHED, ST_META[STATUS.MATCHED].label, C.grn, counts[STATUS.MATCHED])}
        {counts[STATUS.ID_MISMATCH] > 0 && pill(STATUS.ID_MISMATCH, ST_META[STATUS.ID_MISMATCH].label, C.amb, counts[STATUS.ID_MISMATCH])}
        {counts[STATUS.NOT_IN_CATALOG] > 0 && pill(STATUS.NOT_IN_CATALOG, ST_META[STATUS.NOT_IN_CATALOG].label, C.red, counts[STATUS.NOT_IN_CATALOG])}
        {counts.unknown > 0 && pill("unknown", ST_META.unknown.label, "#585d6e", counts.unknown)}
        {counts.gap > 0 && pill("gap", "Price gap ≥8%", C.vio, counts.gap)}

        <div style={{ position: "relative" }}>
          <button onClick={() => { setSubOpen(!subOpen); setSubQ(""); }}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: 7, minWidth: 180, textAlign: "left",
              border: `1px solid ${subSel.length ? C.vio : "var(--border)"}`, background: subSel.length ? C.vio + "14" : "var(--bg-panel, #161820)",
              color: subSel.length ? C.vio : "var(--text-secondary)", fontSize: 11.5, cursor: "pointer", fontWeight: subSel.length ? 600 : 400 }}>
            <span>{subSel.length === 0 ? "All subcategories" : subSel.length === 1 ? subSel[0] : subSel.length + " subcategories"}</span>
            {subSel.length
              ? <span onClick={(e) => { e.stopPropagation(); setSubSel([]); setSubOpen(false); }} style={{ marginLeft: "auto", fontSize: 14 }}>×</span>
              : <span style={{ marginLeft: "auto", fontSize: 9, opacity: .7 }}>▾</span>}
          </button>
          {subOpen && (
            <div onClick={(e) => e.stopPropagation()} style={{ position: "absolute", top: "calc(100% + 5px)", left: 0, zIndex: 40, width: 276, background: "#1c1f2b", border: "1px solid var(--border)", borderRadius: 9, boxShadow: "0 14px 38px rgba(0,0,0,.55)", overflow: "hidden" }}>
              <input autoFocus value={subQ} onChange={(e) => setSubQ(e.target.value)} placeholder="Type to find subcategories…"
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: "none", borderBottom: "1px solid var(--border)", background: "transparent", color: "var(--text-primary)", fontSize: 12, outline: "none" }} />
              <div style={{ display: "flex", gap: 10, padding: "7px 12px", borderBottom: "1px solid var(--border)", fontSize: 10.5 }}>
                <span onClick={() => setSubSel([...new Set([...subSel, ...subOpts.map((o) => o.name)])])} style={{ color: C.vio, cursor: "pointer" }}>Select all shown</span>
                <span onClick={() => setSubSel([])} style={{ color: C.vio, cursor: "pointer" }}>Clear</span>
                <span style={{ marginLeft: "auto", color: "#585d6e", fontFamily: C.mono }}>{subSel.length ? subSel.length + " selected" : ""}</span>
              </div>
              <div style={{ maxHeight: 252, overflowY: "auto", padding: "4px 0" }}>
                {subOpts.length === 0 && <div style={{ padding: "10px 12px", fontSize: 11.5, color: "#585d6e" }}>No subcategory matches that.</div>}
                {subOpts.map((o) => {
                  const on = subSel.includes(o.name);
                  return <div key={o.name} onClick={() => setSubSel(on ? subSel.filter((x) => x !== o.name) : [...subSel, o.name])}
                    style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 12px", fontSize: 12, cursor: "pointer", color: o.n === 0 ? "#585d6e" : "var(--text-primary)" }}>
                    <span style={{ width: 13, height: 13, borderRadius: 3, border: `1px solid ${on ? C.vio : "#585d6e"}`, background: on ? C.vio : "transparent", flexShrink: 0 }} />
                    {o.name}<span style={{ marginLeft: "auto", fontFamily: C.mono, fontSize: 10.5, color: "#585d6e" }}>{o.n}</span>
                  </div>;
                })}
              </div>
            </div>
          )}
        </div>

        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or ID…"
          style={{ padding: "6px 11px", borderRadius: 7, border: "1px solid var(--border)", background: "var(--bg-panel, #161820)", color: "var(--text-primary)", fontSize: 12, width: 170, outline: "none" }} />
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <SelectionBar count={pick.count} total={rows.length} onClear={pick.clear} />
        </span>
        {onExport && (
          <button onClick={() => onExport(pick.count ? rows.filter((r) => pick.sel.has(r.key)) : rows, weeks)} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${C.grn}55`, background: C.grn + "14", color: C.grn, fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
            ↓ Export to Excel{pick.count ? ` (${pick.count})` : ""}
          </button>
        )}
      </div>

      {lpMissing && (
        <div style={{ borderRadius: 8, padding: "10px 13px", fontSize: 11.5, marginBottom: 12, lineHeight: 1.55,
          background: C.amb + "12", border: `1px solid ${C.amb}44`, color: "var(--text-secondary)" }}>
          <strong style={{ color: C.amb }}>No list prices loaded
          {lpMissing !== "none" && lpMissing !== "store" ? ` for ${lpMissing}` : ""}.</strong>{" "}
          The List and Gap columns are blank because the Sales Price Export hasn't been read
          {lpMissing === "none" ? "" : " for these stores"} — not because these items have no price.
          Upload the <span style={{ fontFamily: C.mono }}>Sales Price Export</span> (it must have a date in the
          filename, e.g. <span style={{ fontFamily: C.mono }}>Sales_Price_Export_08_12_26.xlsx</span>).
        </div>
      )}

      {filter !== "all" && (
        <div style={{ borderRadius: 8, padding: "10px 13px", fontSize: 11.5, color: "var(--text-secondary)", marginBottom: 12, lineHeight: 1.55,
          background: (filter === "gap" ? C.vio : ST_META[filter]?.color || "#585d6e") + "12",
          border: `1px solid ${(filter === "gap" ? C.vio : ST_META[filter]?.color || "#585d6e")}33` }}>
          {filter === "gap"
            ? "What the register captured differs from the D365 list price by 8% or more. Below list usually means markdowns; above list usually means a price change never took in the scale."
            : ST_META[filter]?.desc}
        </div>
      )}

      <div style={{ background: "var(--bg-panel, #161820)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto", maxHeight: "68vh", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1010 }}>
            <thead><tr style={{ background: "#1c1f2b", borderBottom: "1px solid var(--border)", position: "sticky", top: 0, zIndex: 2 }}>
              <th style={{ padding: "8px 10px", width: 34 }}>
                <input type="checkbox" checked={pick.allOn} onChange={pick.toggleAll} style={cbStyle}
                  title={pick.allOn ? "Clear selection" : "Select all rows shown"} />
              </th>
              {store === "ALL" && th("Store", "store")}
              {th("Status", "status")}{th("Catapult ID", "id")}{th("Product", "name")}{th("Subcategory", "sub")}
              {th("Qty", "qty", "right")}{th("vs prior", "qtyd", "right")}
              {th("Net amount", "amt", "right")}{th("vs prior", "amtd", "right")}
              {th("Captured", "cap", "right")}{th("List", "list", "right")}{th("Gap", "gap", "right")}
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const m = ST_META[r.status];
                const g = priceGapPct({ qty: r.qtyCur, amt: r.amtCur, listPrice: r.listPrice, placeholder: r.placeholder });
                const big = g != null && Math.abs(g) >= 8;
                return (
                  <tr key={r.key} onClick={() => setDetail(r)}
                      style={{ borderBottom: "1px solid #1a1d24", cursor: "pointer", background: pick.sel.has(r.key) ? "rgba(91,141,217,0.08)" : "transparent" }}>
                    <td style={{ padding: "7px 10px" }} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={pick.sel.has(r.key)} onChange={() => pick.toggle(r.key)} style={cbStyle} />
                    </td>
                    {store === "ALL" && <td style={{ padding: "7px 10px" }}>
                      <span style={{ fontFamily: C.mono, fontSize: 10.5, fontWeight: 700, color: C.blue, background: C.blue + "1a", borderRadius: 4, padding: "1px 6px" }}>{r.store}</span></td>}
                    <td style={{ padding: "7px 10px" }}>
                      <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: m.color, marginRight: 7 }} />
                      <span style={{ fontSize: 10.5, color: m.color, fontWeight: 600 }}>{m.label}</span></td>
                    <td style={{ padding: "7px 10px", fontFamily: C.mono, fontSize: 11.5, color: "var(--text-secondary)" }}>{r.pid}</td>
                    <td style={{ padding: "7px 10px", fontSize: 12.5 }}>{r.name}</td>
                    <td style={{ padding: "7px 10px", fontSize: 11, color: "var(--text-secondary)" }}>{r.sub}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: C.mono, fontSize: 11.5 }}>{qf(r.qtyCur)}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}><Delta d={delta(r.qtyPrev, r.qtyCur)} /></td>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: C.mono, fontSize: 11.5, fontWeight: 600 }}>{money(r.amtCur)}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}><Delta d={delta(r.amtPrev, r.amtCur)} /></td>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: C.mono, fontSize: 11.5, color: C.amb }}>
                      {r.qtyCur && r.amtCur != null && r.qtyCur !== 0 ? money(toCents(r.amtCur / r.qtyCur)) : "—"}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: C.mono, fontSize: 11.5, color: C.vio }}>
                      {r.placeholder ? <span style={{ color: C.amb, fontSize: 10.5 }}>no price</span> : money(toCents(r.listPrice))}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: C.mono, fontSize: 11,
                      color: r.placeholder || g == null ? "#585d6e" : !big ? "#585d6e" : g < 0 ? C.red : C.amb, fontWeight: big ? 600 : 400 }}>
                      {r.placeholder || g == null ? "—" : (g > 0 ? "+" : "") + g.toFixed(0) + "%"}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={store === "ALL" ? 12 : 11} style={{ padding: 28, textAlign: "center", color: "#585d6e" }}>Nothing matches those filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ marginTop: 10, fontSize: 10.5, color: "#585d6e" }}>
        Showing {rows.length} of {all.length}{subSel.length ? ` · subcategories: ${subSel.join(", ")}` : ""} · click a column to sort, click again to reverse.
      </div>
      {detail && (
        <ModalBoundary onClose={() => setDetail(null)}>
          <SalesDetail row={detail} weeks={weeks} onClose={() => setDetail(null)} />
        </ModalBoundary>
      )}
      {totalsFocus && (
        <ModalBoundary onClose={() => setTotalsFocus(null)}>
        <TotalsDetail rows={rows} weeks={weeks} store={store} focus={totalsFocus}
          scope={[filter !== "all" ? (filter === "gap" ? "price gap \u22658%" : (ST_META[filter]?.label || filter).toLowerCase()) : null,
                  subSel.length ? (subSel.length === 1 ? subSel[0] : subSel.length + " subcategories") : null,
                  q.trim() ? `search "${q.trim()}"` : null].filter(Boolean).join(" \u00b7 ") || "all items"}
          onClose={() => setTotalsFocus(null)} />
        </ModalBoundary>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// AUDIT TAB — three buckets. The two "sold" buckets are derived from the same
// rows the Sales tab uses, so the tabs can't disagree.
// ══════════════════════════════════════════════════════════════════════════
const BK = {
  soldIn: { label: "Sold / in catalog", color: C.grn, hint: "The register and the catalog agree.",
    note: "These are healthy — nothing to action. The count matters mainly as the denominator for the other two." },
  notSold: { label: "Not sold / in catalog", color: C.red, hint: "Carried on paper, sold nothing in the loaded weeks.",
    note: "Delisting candidates — but read Catalog status first. An item marked STOCK that sold nothing is a real question; SEASONAL or PRE-ORDER may be dormant for good reason. With only a few weeks loaded, a monthly seller looks dead here." },
  soldNotIn: { label: "Sold / not in catalog", color: C.amb, hint: "Ringing up with no catalog match.",
    note: "Either a scale item you don't purchase against, or an unmapped ID. This revenue is real but invisible to purchasing." },
};

export function AuditTab({ salesWeeks, catalogIndex, listPrices, store, onExport }) {
  const [bucket, setBucket] = useState("notSold");
  const [q, setQ] = useState("");
  const [stockOnly, setStockOnly] = useState(false);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState(1);

  // Audit rows are exactly the rows whose IDs are broken, so the key has to
  // degrade gracefully: Catapult ID, else the sales Product Id, else D365.
  // Scoped by bucket because the same item can appear under different buckets.
  const auditKeyOf = useCallback((r) => `${bucket}|${r.store}|${r.pid || r.d365 || r.name}`, [bucket]);

  const stores = store === "ALL" ? Object.keys(catalogIndex || {}) : [store];
  // Collapse all loaded weeks: an item counts as sold if it moved in ANY of them.
  const salesByStore = useMemo(() => {
    const out = {};
    for (const s of stores) {
      const merged = {};
      Object.values(salesWeeks?.[s] || {}).forEach((wk) => Object.assign(merged, wk));
      out[s] = merged;
    }
    return out;
  }, [salesWeeks, stores.join(",")]);

  const built = useMemo(() => buildSalesAndAudit({ stores, salesByStore, catalogByStore: catalogIndex, prices: listPrices }),
    [stores.join(","), salesByStore, catalogIndex, listPrices]);

  const counts = useMemo(() => ({
    soldIn: built.sales.filter((r) => r.status && r.status !== STATUS.NOT_IN_CATALOG).length,
    notSold: built.catalogOnly.length,
    soldNotIn: built.sales.filter((r) => r.status === STATUS.NOT_IN_CATALOG).length,
  }), [built]);

  const rows = useMemo(() => {
    let out = bucket === "notSold" ? built.catalogOnly
      : built.sales.filter((r) => bucket === "soldNotIn" ? r.status === STATUS.NOT_IN_CATALOG : r.status && r.status !== STATUS.NOT_IN_CATALOG);
    if (bucket === "notSold" && stockOnly) out = out.filter((r) => (r.catalogStatus || "").toUpperCase().startsWith("STOCK"));
    if (q.trim()) {
      const k = q.trim().toLowerCase();
      out = out.filter((r) => (r.name || "").toLowerCase().includes(k) || (r.pid || "").includes(k) || (r.d365 || "").includes(k));
    }
    const val = (r) => ({ store: r.store, cid: r.pid, d365: r.d365, name: r.name, sub: r.sub, qty: r.qty, net: r.amt, list: r.listPrice, status: r.catalogStatus })[sortKey];
    return [...out].sort((a, b) => {
      const x = val(a), y = val(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return typeof x === "string" ? sortDir * x.localeCompare(y) : sortDir * (x - y);
    });
  }, [built, bucket, stockOnly, q, sortKey, sortDir]);

  const pick = useRowSelection(rows, auditKeyOf);

  const inCat = counts.soldIn + counts.notSold;
  const b = BK[bucket];
  const th = (label, key, align) => (
    <th onClick={() => { if (sortKey === key) setSortDir(-sortDir); else { setSortKey(key); setSortDir(["qty", "net", "list"].includes(key) ? -1 : 1); } }}
      style={{ textAlign: align || "left", padding: "9px 10px", fontSize: 10, fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: sortKey === key ? C.vio : "var(--text-secondary)", cursor: "pointer", whiteSpace: "nowrap" }}>
      {label}{sortKey === key ? (sortDir < 0 ? " ▼" : " ▲") : ""}
    </th>
  );

  if (!inCat) return (
    <div style={{ padding: 40, textAlign: "center", color: "var(--text-secondary)" }}>
      Audit needs a catalog and a sales report. Upload both above and this fills in.
    </div>
  );

  return (
    <div style={{ padding: "16px 28px 40px" }}>
      <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginBottom: 14 }}>
        {store === "ALL" ? `${stores.length} stores` : store} · {inCat.toLocaleString()} catalog items · an item counts as sold if it moved in any loaded week
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
        {["soldIn", "notSold", "soldNotIn"].map((k) => {
          const m = BK[k];
          return (
            <div key={k} onClick={() => { setBucket(k); setStockOnly(false); setSortKey(k === "notSold" ? "name" : "net"); setSortDir(k === "notSold" ? 1 : -1); }}
              style={{ background: bucket === k ? "#191c25" : "var(--bg-panel, #161820)", border: `1px solid ${bucket === k ? m.color : "var(--border)"}`, borderRadius: 10, padding: "13px 16px", cursor: "pointer" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 700, color: m.color }}>{m.label}</div>
              <div style={{ fontFamily: C.mono, fontSize: 24, fontWeight: 700, color: m.color, marginTop: 4 }}>
                {counts[k].toLocaleString()}
                {k !== "soldNotIn" && inCat > 0 && <span style={{ fontFamily: C.mono, fontSize: 11, color: "#585d6e", marginLeft: 7 }}>{Math.round((100 * counts[k]) / inCat)}% of catalog</span>}
              </div>
              <div style={{ fontSize: 10.5, color: "#585d6e", marginTop: 4, lineHeight: 1.45 }}>{m.hint}</div>
            </div>
          );
        })}
      </div>
      <div style={{ borderRadius: 8, padding: "10px 13px", fontSize: 11.5, color: "var(--text-secondary)", marginBottom: 12, lineHeight: 1.55, background: b.color + "12", border: `1px solid ${b.color}33` }}>
        {b.note}
      </div>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        {bucket === "notSold" && (
          <button onClick={() => setStockOnly(!stockOnly)}
            style={{ padding: "5px 12px", borderRadius: 7, fontSize: 11.5, cursor: "pointer", fontWeight: stockOnly ? 700 : 500,
              border: `1px solid ${stockOnly ? C.red : "var(--border)"}`, background: stockOnly ? C.red + "1a" : "transparent", color: stockOnly ? C.red : "var(--text-secondary)" }}>
            Status = STOCK only <span style={{ fontFamily: C.mono, opacity: .75 }}>
              {built.catalogOnly.filter((r) => (r.catalogStatus || "").toUpperCase().startsWith("STOCK")).length}</span>
          </button>
        )}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or ID…"
          style={{ padding: "6px 11px", borderRadius: 7, border: "1px solid var(--border)", background: "var(--bg-panel, #161820)", color: "var(--text-primary)", fontSize: 12, width: 180, outline: "none" }} />
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <SelectionBar count={pick.count} total={rows.length} onClear={pick.clear} />
        </span>
        {onExport && (
          <button onClick={() => onExport(pick.count ? rows.filter((r) => pick.sel.has(auditKeyOf(r))) : rows, bucket)} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${C.grn}55`, background: C.grn + "14", color: C.grn, fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
            ↓ Export to Excel{pick.count ? ` (${pick.count})` : ""}
          </button>
        )}
      </div>
      <div style={{ background: "var(--bg-panel, #161820)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto", maxHeight: "68vh", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead><tr style={{ background: "#1c1f2b", borderBottom: "1px solid var(--border)", position: "sticky", top: 0, zIndex: 2 }}>
              <th style={{ padding: "8px 10px", width: 34 }}>
                <input type="checkbox" checked={pick.allOn} onChange={pick.toggleAll} style={cbStyle}
                  title={pick.allOn ? "Clear selection" : "Select all rows shown"} />
              </th>
              {store === "ALL" && th("Store", "store")}
              {th("Catapult ID", "cid")}{th("D365", "d365")}{th("Product", "name")}
              {bucket !== "notSold" && th("Subcategory", "sub")}
              {bucket !== "notSold" && th("Qty", "qty", "right")}
              {bucket !== "notSold" && th("Net amount", "net", "right")}
              {th("List price", "list", "right")}
              {bucket === "notSold" && th("Catalog status", "status")}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const isStock = (r.catalogStatus || "").toUpperCase().startsWith("STOCK");
                return (
                  <tr key={r.store + "|" + (r.pid || r.d365) + "|" + i}
                      style={{ borderBottom: "1px solid #1a1d24", background: pick.sel.has(auditKeyOf(r)) ? "rgba(91,141,217,0.08)" : "transparent" }}>
                    <td style={{ padding: "7px 10px" }}>
                      <input type="checkbox" checked={pick.sel.has(auditKeyOf(r))} onChange={() => pick.toggle(auditKeyOf(r))} style={cbStyle} />
                    </td>
                    {store === "ALL" && <td style={{ padding: "7px 10px" }}>
                      <span style={{ fontFamily: C.mono, fontSize: 10.5, fontWeight: 700, color: C.vio, background: C.vio + "1a", borderRadius: 4, padding: "1px 6px" }}>{r.store}</span></td>}
                    <td style={{ padding: "7px 10px", fontFamily: C.mono, fontSize: 11.5, color: "var(--text-secondary)" }}>{r.pid || "—"}</td>
                    <td style={{ padding: "7px 10px", fontFamily: C.mono, fontSize: 11.5, color: "#585d6e" }}>{r.d365 || "—"}</td>
                    <td style={{ padding: "7px 10px", fontSize: 12.5 }}>{r.name}
                      {r.catalogName && <span style={{ color: "#585d6e", fontSize: 11 }}> · {r.catalogName}</span>}</td>
                    {bucket !== "notSold" && <td style={{ padding: "7px 10px", fontSize: 11, color: "var(--text-secondary)" }}>{r.sub || "—"}</td>}
                    {bucket !== "notSold" && <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: C.mono, fontSize: 11.5 }}>{qf(r.qty)}</td>}
                    {bucket !== "notSold" && <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: C.mono, fontSize: 11.5, fontWeight: 600 }}>{money(toCents(r.amt))}</td>}
                    <td style={{ padding: "7px 10px", textAlign: "right", fontFamily: C.mono, fontSize: 11.5, color: C.vio }}>
                      {r.placeholder ? <span style={{ color: C.amb, fontSize: 10.5 }}>no price</span> : money(toCents(r.listPrice))}</td>
                    {bucket === "notSold" && <td style={{ padding: "7px 10px" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 6px", color: isStock ? C.red : "#8b90a0", background: (isStock ? C.red : "#8b90a0") + "1a" }}>
                        {r.catalogStatus || "—"}</span></td>}
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={10} style={{ padding: 28, textAlign: "center", color: "#585d6e" }}>Nothing matches.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ marginTop: 10, fontSize: 10.5, color: "#585d6e" }}>
        Showing {rows.length.toLocaleString()} rows{bucket === "notSold" && stockOnly ? " (STOCK only)" : ""} · click a column to sort.
      </div>
    </div>
  );
}
